import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { CostRow, CostUnit } from './types'

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

export const emptyRow = (): CostRow => ({
  id: newId(),
  name: '',
  unit: 'г',
  packAmount: null,
  packPrice: null,
  usedAmount: null,
})

interface CostCalculatorState {
  dessertName: string
  rows: CostRow[]
  setDessertName: (name: string) => void
  updateRow: (id: string, patch: Partial<CostRow>) => void
  /** Выбор продукта из каталога подставляет и единицу по умолчанию. */
  pickProduct: (id: string, name: string, unit?: CostUnit) => void
  addRow: () => void
  removeRow: (id: string) => void
  reset: () => void
}

/**
 * Состояние расчёта себестоимости с автосохранением черновика.
 *
 * Отдельный стор фичи, а не часть useAppStore: это не общее состояние
 * приложения, а данные одного экрана — в глобальном сторе они бы только мешали
 * и сериализовались вместе с профилем на каждое нажатие клавиши.
 *
 * Черновик держим в localStorage (не в session): экран легко закрыть случайно,
 * а вводить полтора десятка позиций заново — верный способ сюда не вернуться.
 */
export const useCostCalculatorStore = create<CostCalculatorState>()(
  persist(
    (set) => ({
      dessertName: '',
      rows: [emptyRow()],

      setDessertName: (dessertName) => set({ dessertName }),

      updateRow: (id, patch) =>
        set((state) => ({
          rows: state.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
        })),

      pickProduct: (id, name, unit) =>
        set((state) => ({
          rows: state.rows.map((r) =>
            r.id === id ? { ...r, name, ...(unit ? { unit } : {}) } : r
          ),
        })),

      addRow: () => set((state) => ({ rows: [...state.rows, emptyRow()] })),

      // Последнюю строку не удаляем, а очищаем: пустая таблица выглядит поломкой.
      removeRow: (id) =>
        set((state) => ({
          rows:
            state.rows.length > 1
              ? state.rows.filter((r) => r.id !== id)
              : [emptyRow()],
        })),

      reset: () => set({ dessertName: '', rows: [emptyRow()] }),
    }),
    {
      name: 'pauline:cost-calculator',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        dessertName: state.dessertName,
        rows: state.rows,
      }),
      // Пустой массив мог приехать из битого черновика — экран без строк
      // выглядел бы сломанным.
      onRehydrateStorage: () => (state) => {
        if (state && state.rows.length === 0) state.rows = [emptyRow()]
      },
    }
  )
)
